import { supabase } from '@/lib/supabaseClient';
import { ensureAuthenticatedSession } from '@/utils/authentication';
import { User } from '@/types';
import {
  AccountClosureEligibility,
  PrivacyRightsRequest,
  PrivacyRightsResolutionPayload,
  PrivacyRightType,
  PrivacyRequestStatus,
} from '@/types/privacyRights';
import { signInWithEmail, signOut as supabaseSignOut } from '@/services/authService';
import { logAuthEvent } from '@/services/authAuditService';
import { isRecord, toOptionalString } from '@/utils/typeGuards';

export const checkAccountClosureEligibility = async (
  user: User,
  communityId?: string | null
): Promise<AccountClosureEligibility> => {
  const targetCommunityId = communityId ?? user.communityId ?? null;
  const memberships = user.communityMemberships ?? [];
  const activeMembership = targetCommunityId
    ? memberships.find((m) => m.communityId === targetCommunityId)
    : memberships[0];
  const effectiveRole = activeMembership?.role ?? user.role;

  // 1. Bloqueo si es Administrador de la plataforma (admin), Administrador de la comunidad (manager) o Presidente (president)
  if (effectiveRole === 'admin' || effectiveRole === 'manager' || effectiveRole === 'president') {
    const roleLabel =
      effectiveRole === 'admin'
        ? 'Administrador de la plataforma'
        : effectiveRole === 'manager'
        ? 'Administrador de la comunidad'
        : 'Presidente';

    return {
      canClose: false,
      blockerReason: `No puedes darte de baja mientras figures como ${roleLabel}. Debes transferir tu cargo o rol a otro usuario antes de cerrar tu cuenta.`,
      isManagementRole: true,
      isOnlyCommunityManager: false,
      hasPastDueDebt: false,
      hasFutureReservations: false,
      futureReservationsCount: 0,
      hasOpenIncidents: false,
      openIncidentsCount: 0,
    };
  }

  // 2. Comprobar deuda pendiente
  const hasPastDueDebt =
    user.isDefaulter === true || activeMembership?.isDefaulter === true;

  // 3. Comprobar reservas futuras
  let futureReservationsCount = 0;
  try {
    const nowIso = new Date().toISOString();
    const { count, error } = await supabase
      .from('reservations')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .gt('start_time', nowIso);

    if (!error && typeof count === 'number') {
      futureReservationsCount = count;
    }
  } catch (err) {
    console.warn('Error al verificar reservas futuras:', err);
  }

  // 4. Comprobar incidencias abiertas reportadas
  let openIncidentsCount = 0;
  try {
    const { count, error } = await supabase
      .from('incidents')
      .select('id', { count: 'exact', head: true })
      .eq('created_by', user.id)
      .in('status', ['pending', 'in-progress', 'in_progress']);

    if (!error && typeof count === 'number') {
      openIncidentsCount = count;
    }
  } catch (err) {
    console.warn('Error al verificar incidencias abiertas:', err);
  }

  const dwellingSummary = [
    activeMembership?.block ? `Bloque ${activeMembership.block}` : null,
    activeMembership?.floor ? `Piso ${activeMembership.floor}` : null,
    activeMembership?.letter ? `Letra ${activeMembership.letter}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return {
    canClose: true,
    isManagementRole: false,
    isOnlyCommunityManager: false,
    hasPastDueDebt,
    hasFutureReservations: futureReservationsCount > 0,
    futureReservationsCount,
    hasOpenIncidents: openIncidentsCount > 0,
    openIncidentsCount,
    dwellingSummary: dwellingSummary || undefined,
  };
};

export interface RequestAccountClosureResult {
  success: boolean;
  effectiveDate: Date;
  graceDays: number;
}

export const requestAccountClosure = async (options: {
  user: User;
  password: string;
  communityId?: string | null;
}): Promise<RequestAccountClosureResult> => {
  const { user, password, communityId } = options;
  const targetCommunityId = communityId ?? user.communityId ?? null;

  // 1. Validar que la contraseña sea proporcionada
  if (!password || password.trim().length === 0) {
    throw new Error('Debes introducir tu contraseña actual para confirmar el cierre de cuenta.');
  }

  // 2. Comprobar bloqueos
  const eligibility = await checkAccountClosureEligibility(user, communityId);
  if (!eligibility.canClose) {
    throw new Error(eligibility.blockerReason || 'No es posible cerrar la cuenta en este momento.');
  }

  // 3. Re-autenticación obligatoria con contraseña
  try {
    await signInWithEmail(user.email, password);
  } catch (err) {
    void logAuthEvent({
      action: 'auth.account_closure_failed',
      severity: 'warning',
      email: user.email,
      userId: user.id,
      details: { reason: 'invalid_reauth_password' },
    });
    if (err instanceof Error && err.message.includes('contraseña')) {
      throw err;
    }
    throw new Error('La contraseña introducida no es correcta.');
  }

  const now = new Date();
  const effectiveDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  // 4. Cancelar reservas futuras automáticamente
  try {
    await supabase
      .from('reservations')
      .delete()
      .eq('user_id', user.id)
      .gt('start_time', now.toISOString());
  } catch (err) {
    console.warn('Error al cancelar reservas futuras del usuario en baja:', err);
  }

  // 5. Actualizar estado a pending_deletion
  const { error: updateError } = await supabase
    .from('users')
    .update({
      status: 'pending_deletion',
      deletion_requested_at: now.toISOString(),
      deletion_effective_at: effectiveDate.toISOString(),
    })
    .eq('id', user.id);

  if (updateError) {
    throw new Error(`Error al registrar el cierre de cuenta: ${updateError.message}`);
  }

  // 6. Registrar en auditoría
  void logAuthEvent({
    action: 'auth.account_closure_requested',
    severity: 'info',
    email: user.email,
    userId: user.id,
    communityId: targetCommunityId,
    details: {
      requestedAt: now.toISOString(),
      effectiveDeletionAt: effectiveDate.toISOString(),
      gracePeriodDays: 30,
      hadPastDueDebt: eligibility.hasPastDueDebt,
      cancelledReservations: eligibility.futureReservationsCount,
    },
  });

  // 7. Invalidar sesiones globales
  // Nota: Las notificaciones in-app y el envío de correo de confirmación
  // se disparan automáticamente en PostgreSQL mediante el trigger
  // trg_notify_on_user_account_closure sobre la tabla 'users'.
  try {
    await supabaseSignOut();
  } catch {
    // Ignorar fallo secundario de logout
  }

  return {
    success: true,
    effectiveDate,
    graceDays: 30,
  };
};

export const reactivateAccount = async (user: User): Promise<boolean> => {
  await ensureAuthenticatedSession();

  // 1. Intentar reactivación mediante la función RPC SECURITY DEFINER del servidor
  if (typeof supabase.rpc === 'function') {
    const { error: rpcError } = await supabase.rpc('reactivate_own_account');
    if (!rpcError) {
      void logAuthEvent({
        action: 'auth.account_reactivated',
        severity: 'info',
        email: user.email,
        userId: user.id,
        details: {
          reactivatedAt: new Date().toISOString(),
          method: 'rpc',
        },
      });
      return true;
    }

    // Si la función RPC no existe en la BD (código 42883), continuamos con fallback de actualización directa
    const isFunctionNotFound =
      rpcError.code === '42883' ||
      rpcError.message?.toLowerCase().includes('function') ||
      rpcError.message?.toLowerCase().includes('does not exist');

    if (!isFunctionNotFound) {
      throw new Error(`Error al reactivar la cuenta: ${rpcError.message}`);
    }
  }

  // 2. Fallback: actualización directa en users con comprobación explícita de filas afectadas
  const { data: updatedRows, error } = await supabase
    .from('users')
    .update({
      status: 'active',
      deletion_requested_at: null,
      deletion_effective_at: null,
    })
    .eq('id', user.id)
    .select('id, status');

  if (error) {
    throw new Error(`Error al reactivar la cuenta: ${error.message}`);
  }

  // Comprobar que realmente se actualizó la fila (evita falsos positivos cuando RLS filtra las filas)
  if (Array.isArray(updatedRows) && updatedRows.length === 0) {
    throw new Error('No se pudo reactivar la cuenta en la base de datos (0 registros modificados). Comprueba las políticas de usuario.');
  }

  void logAuthEvent({
    action: 'auth.account_reactivated',
    severity: 'info',
    email: user.email,
    userId: user.id,
    details: {
      reactivatedAt: new Date().toISOString(),
      method: 'direct_update',
    },
  });

  return true;
};

const mapDbRightsRequest = (row: Record<string, unknown>): PrivacyRightsRequest => {
  const itemsErased = Array.isArray(row.items_erased)
    ? row.items_erased.filter((item): item is string => typeof item === 'string')
    : [];
  const itemsRetained = Array.isArray(row.items_retained)
    ? row.items_retained.filter((item): item is string => typeof item === 'string')
    : [];

  return {
    id: typeof row.id === 'string' ? row.id : '',
    userId: toOptionalString(row.user_id) ?? null,
    communityId: toOptionalString(row.community_id) ?? null,
    userName: typeof row.user_name === 'string' ? row.user_name : '—',
    userEmail: typeof row.user_email === 'string' ? row.user_email : '',
    userRole: (typeof row.user_role === 'string' ? row.user_role : 'neighbor') as User['role'],
    dwellingInfo: toOptionalString(row.dwelling_info) ?? null,
    requestType: (typeof row.request_type === 'string' ? row.request_type : 'erasure') as PrivacyRightType,
    status: (typeof row.status === 'string' ? row.status : 'pending') as PrivacyRequestStatus,
    details: toOptionalString(row.details) ?? null,
    createdAt: typeof row.created_at === 'string' ? row.created_at : new Date().toISOString(),
    deadlineAt: typeof row.deadline_at === 'string' ? row.deadline_at : new Date().toISOString(),
    resolvedAt: toOptionalString(row.resolved_at) ?? null,
    resolvedBy: toOptionalString(row.resolved_by) ?? null,
    resolutionNotes: toOptionalString(row.resolution_notes) ?? null,
    itemsErased,
    itemsRetained,
    legalGrounds: toOptionalString(row.legal_grounds) ?? null,
    appealRightsNotified: row.appeal_rights_notified !== false,
  };
};

export const createPrivacyRightsRequest = async (payload: {
  user: User;
  communityId?: string | null;
  requestType: PrivacyRightType;
  details?: string;
}): Promise<PrivacyRightsRequest> => {
  await ensureAuthenticatedSession();
  const { user, communityId, requestType, details } = payload;

  const targetCommunityId = communityId ?? user.communityId ?? null;
  const memberships = user.communityMemberships ?? [];
  const activeMembership = targetCommunityId
    ? memberships.find((m) => m.communityId === targetCommunityId)
    : memberships[0];

  const dwellingInfo = [
    activeMembership?.block ? `Bloque ${activeMembership.block}` : null,
    activeMembership?.floor ? `Piso ${activeMembership.floor}` : null,
    activeMembership?.letter ? `Letra ${activeMembership.letter}` : null,
  ]
    .filter(Boolean)
    .join(' · ') || null;

  const now = new Date();
  const deadlineAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  const insertData = {
    user_id: user.id,
    community_id: targetCommunityId,
    user_name: [user.name, user.lastName || user.last_name].filter(Boolean).join(' ') || user.email,
    user_email: user.email,
    user_role: activeMembership?.role ?? user.role,
    dwelling_info: dwellingInfo,
    request_type: requestType,
    status: 'pending',
    details: details?.trim() || null,
    created_at: now.toISOString(),
    deadline_at: deadlineAt.toISOString(),
    items_erased: [],
    items_retained: [],
    appeal_rights_notified: true,
  };

  const response = await supabase
    .from('privacy_rights_requests')
    .insert(insertData)
    .select('*')
    .single();

  const data: unknown = response.data;
  const error = response.error;

  if (error || !data || !isRecord(data)) {
    throw new Error(`Error al registrar la solicitud de derechos: ${error?.message ?? 'Sin respuesta'}`);
  }

  void logAuthEvent({
    action: 'gdpr.rights_request_created',
    severity: 'critical',
    email: user.email,
    userId: user.id,
    communityId: targetCommunityId,
    details: {
      requestType,
      userName: insertData.user_name,
      userEmail: user.email,
      deadlineAt: deadlineAt.toISOString(),
    },
  });

  return mapDbRightsRequest(data as Record<string, unknown>);
};

export const getPrivacyRightsRequests = async (options?: {
  communityId?: string | null;
  userId?: string | null;
}): Promise<PrivacyRightsRequest[]> => {
  await ensureAuthenticatedSession();
  let query = supabase
    .from('privacy_rights_requests')
    .select('*')
    .order('created_at', { ascending: false });

  if (options?.communityId) {
    query = query.eq('community_id', options.communityId);
  }
  if (options?.userId) {
    query = query.eq('user_id', options.userId);
  }

  const { data, error } = await query;
  if (error) {
    throw new Error(`Error al consultar solicitudes de derechos: ${error.message}`);
  }

  const rows = Array.isArray(data) ? data.filter(isRecord) : [];
  return rows.map(mapDbRightsRequest);
};

export const resolvePrivacyRightsRequest = async (
  requestId: string,
  payload: PrivacyRightsResolutionPayload,
  resolverUser: User
): Promise<PrivacyRightsRequest> => {
  await ensureAuthenticatedSession();

  if (resolverUser.role !== 'admin') {
    throw new Error('Solo el administrador de la plataforma puede resolver solicitudes de derechos RGPD.');
  }

  const now = new Date();

  const updateData = {
    status: payload.status,
    resolved_at: now.toISOString(),
    resolved_by: resolverUser.id,
    resolution_notes: payload.resolutionNotes.trim(),
    items_erased: payload.itemsErased,
    items_retained: payload.itemsRetained,
    legal_grounds: payload.legalGrounds.trim(),
    appeal_rights_notified: true,
  };

  const response = await supabase
    .from('privacy_rights_requests')
    .update(updateData)
    .eq('id', requestId)
    .select('*')
    .single();

  const data: unknown = response.data;
  const error = response.error;

  if (error || !data || !isRecord(data)) {
    throw new Error(`Error al resolver la solicitud de derechos: ${error?.message ?? 'Sin respuesta'}`);
  }

  void logAuthEvent({
    action: 'gdpr.rights_request_resolved',
    severity: 'info',
    email: resolverUser.email,
    userId: resolverUser.id,
    details: {
      requestId,
      status: payload.status,
      itemsErasedCount: payload.itemsErased.length,
      itemsRetainedCount: payload.itemsRetained.length,
    },
  });

  return mapDbRightsRequest(data as Record<string, unknown>);
};
